import {
  type IntegrationProvider,
  isIntegrationProvider,
} from '@brew/integration-contracts'
import type { IntegrationMeta } from '@/lib/integrations/catalog/types'
import { isIntegrationAvailable } from './catalog/data'

export type IntegrationCatalogSource = Pick<
  IntegrationMeta,
  'id' | 'name' | 'dataDirection' | 'availability'
>

export type IntegrationCatalogRow = {
  provider: IntegrationProvider
  name: string
  category: IntegrationMeta['dataDirection']
  connected: boolean
  comingSoon?: true
}

/**
 * Remove stale active rows for providers the canonical catalog blocks. Use
 * this at every connected-only consumer of the low-level Convex list query.
 */
export function filterAvailableIntegrationConnections<
  Row extends { provider: string },
>(rows: ReadonlyArray<Row>): Array<Row & { provider: IntegrationProvider }> {
  return rows.filter(
    (row): row is Row & { provider: IntegrationProvider } =>
      isIntegrationProvider(row.provider) &&
      isIntegrationAvailable(row.provider)
  )
}

/**
 * Project the product catalog onto the public list shape. Canonical catalog
 * availability always wins over persisted connection state: a stale active
 * row cannot make a blocked provider reachable again. `comingSoon` remains
 * the compatibility projection consumed by existing clients.
 */
export function projectIntegrationCatalog({
  catalog,
  connectedProviders,
}: {
  catalog: ReadonlyArray<IntegrationCatalogSource>
  connectedProviders: ReadonlySet<string>
}): Array<IntegrationCatalogRow> {
  return catalog.map((meta) => {
    const isComingSoon = meta.availability === 'coming_soon'
    const isConnected = !isComingSoon && connectedProviders.has(meta.id)
    return {
      provider: meta.id,
      name: meta.name,
      category: meta.dataDirection,
      connected: isConnected,
      ...(isComingSoon ? { comingSoon: true as const } : {}),
    }
  })
}
